import { useCallback, useEffect, useState } from 'react';
import axiosConn from '../../axiosConn';
import { getHitLabApiMessage } from './hitLabUtils';

const PROGRAMMING_DEFAULTS = {
  slot: 'live_capsule_primary',
  status: 'draft',
  eyebrow: '',
  message: '',
  ctaLabel: '',
  ctaUrl: '',
  startsAt: '',
  expiresAt: '',
};

const getProgrammingMessage = (response) =>
  response?.data?.data?.programmingMessage || PROGRAMMING_DEFAULTS;

const getFieldValue = (item = {}, field) =>
  item[field] === null || item[field] === undefined ? '' : item[field];

const getFormFromMessage = (message = {}) => ({
  slot: getFieldValue(message, 'slot') || 'live_capsule_primary',
  status: getFieldValue(message, 'status') || 'draft',
  eyebrow: getFieldValue(message, 'eyebrow'),
  message: getFieldValue(message, 'message'),
  ctaLabel: getFieldValue(message, 'ctaLabel'),
  ctaUrl: getFieldValue(message, 'ctaUrl'),
  startsAt: getFieldValue(message, 'startsAt'),
  expiresAt: getFieldValue(message, 'expiresAt'),
});

const toTime = (value) => {
  const time = new Date(value || '').getTime();
  return Number.isFinite(time) ? time : null;
};

export const getProgrammingMessageStateLabel = (
  form = PROGRAMMING_DEFAULTS,
  now = Date.now(),
) => {
  if (form.status === 'draft') return 'Draft - not public';
  if (form.status === 'archived') return 'Archived - not public';
  if (form.status !== 'active') return 'Unknown - not public';

  const startsAt = toTime(form.startsAt);
  const expiresAt = toTime(form.expiresAt);
  if (startsAt && startsAt > now) return 'Scheduled - not live yet';
  if (expiresAt && expiresAt <= now) return 'Expired - not public';
  if (!String(form.message || '').trim()) return 'Active - message required';
  return 'Active - public';
};

export default function HitLabRadioProgrammingMessageManager({ authConfig }) {
  const [form, setForm] = useState(PROGRAMMING_DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadMessage = useCallback(async () => {
    if (!authConfig) return;
    setLoading(true);
    setError('');
    try {
      const response = await axiosConn.get(
        'hitlab/admin/radio/programming-message',
        authConfig,
      );
      setForm(getFormFromMessage(getProgrammingMessage(response)));
    } catch (err) {
      setError(
        getHitLabApiMessage(
          err,
          'Radio Live Capsule message could not be loaded.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  useEffect(() => {
    void loadMessage();
  }, [loadMessage]);

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (form.status === 'active' && !form.message.trim()) {
      return 'Active programming messages require a message.';
    }
    if (form.ctaLabel.trim() && !form.ctaUrl.trim()) {
      return 'CTA URL is required when CTA label is set.';
    }
    if (form.ctaUrl.trim() && !form.ctaLabel.trim()) {
      return 'CTA label is required when CTA URL is set.';
    }
    return '';
  };

  const submitMessage = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    if (!authConfig) {
      setError('Please sign in again to continue.');
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        slot: 'live_capsule_primary',
        status: form.status,
        eyebrow: form.eyebrow.trim(),
        message: form.message.trim(),
        ctaLabel: form.ctaLabel.trim(),
        ctaUrl: form.ctaUrl.trim(),
        startsAt: form.startsAt.trim(),
        expiresAt: form.expiresAt.trim(),
      };
      const response = await axiosConn.post(
        'hitlab/admin/radio/programming-message',
        payload,
        authConfig,
      );
      setForm(getFormFromMessage(getProgrammingMessage(response)));
      setMessage('Live Capsule message saved.');
    } catch (err) {
      setError(
        getHitLabApiMessage(
          err,
          'Radio Live Capsule message could not be saved.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const stateLabel = getProgrammingMessageStateLabel(form);

  return (
    <section
      className="hitlab-radio-programming-admin"
      aria-label="JG Radio Live Capsule Message"
    >
      <div className="hitlab-radio-catalog-admin-header">
        <div>
          <p className="hitlab-kicker">LIVE CAPSULE MESSAGE</p>
          <h2>JG Radio Live Capsule Message</h2>
          <p>{stateLabel}</p>
        </div>
        <button
          type="button"
          className="hitlab-secondary-btn"
          onClick={loadMessage}
          disabled={loading}
        >
          Refresh message
        </button>
      </div>

      {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
      {message ? (
        <div className="hitlab-alert hitlab-alert-success">{message}</div>
      ) : null}

      <div className="hitlab-radio-programming-grid">
        <form
          className="hitlab-radio-catalog-form"
          onSubmit={submitMessage}
        >
          <div className="hitlab-listen-form-grid">
            <label>
              Slot
              <input value="live_capsule_primary" readOnly />
            </label>
            <label>
              Status
              <select
                value={form.status}
                onChange={(event) => updateForm('status', event.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <label>
              Eyebrow
              <input
                value={form.eyebrow}
                maxLength="32"
                onChange={(event) => updateForm('eyebrow', event.target.value)}
              />
            </label>
            <label className="hitlab-form-wide">
              Message
              <textarea
                value={form.message}
                maxLength="120"
                onChange={(event) => updateForm('message', event.target.value)}
              />
            </label>
            <label>
              CTA label
              <input
                value={form.ctaLabel}
                maxLength="32"
                onChange={(event) => updateForm('ctaLabel', event.target.value)}
              />
            </label>
            <label>
              CTA URL
              <input
                value={form.ctaUrl}
                maxLength="240"
                onChange={(event) => updateForm('ctaUrl', event.target.value)}
              />
            </label>
            <label>
              Starts at
              <input
                value={form.startsAt}
                onChange={(event) => updateForm('startsAt', event.target.value)}
              />
            </label>
            <label>
              Expires at
              <input
                value={form.expiresAt}
                onChange={(event) => updateForm('expiresAt', event.target.value)}
              />
            </label>
          </div>

          <div className="hitlab-listen-actions">
            <button
              type="submit"
              className="hitlab-primary-btn"
              disabled={saving}
            >
              {saving ? 'Saving' : 'Save Live Capsule Message'}
            </button>
          </div>
        </form>

        <aside
          className="hitlab-radio-programming-preview"
          aria-label="Live Capsule Message Preview"
        >
          <span>Capsule state</span>
          <strong>{stateLabel}</strong>
          <p className="hitlab-kicker">{form.eyebrow || 'Joe Galaxy Radio'}</p>
          <h3>{form.message || 'No active programming message.'}</h3>
          {form.ctaLabel ? <p>{form.ctaLabel}</p> : null}
        </aside>
      </div>
    </section>
  );
}
